/*! LBJ Wishlist v2.4 — bubble fully hidden at 0, no “0” text, instant mobile toggle */
;(() => {
  const KEY = "lbj_wishlist_v1"
  const READY_CLASS = "wl-ready"
  const S = {
    btn: "[data-wishlist-btn]",
    count: "[data-wishlist-count]",
    grid: "#wishlist-grid",
    empty: "#wishlist-empty",
    toast: "#wishlist-toast",
    open: "[data-wishlist-open]",
    close: "[data-wishlist-close]",
    drawer: "#WishlistDrawer",
    dialog: "#WishlistDrawer .cart-drawer__dialog",
  }

  // ---------------- state ----------------
  const state = {
    get() {
      try {
        return JSON.parse(localStorage.getItem(KEY)) || []
      } catch (_) {
        return []
      }
    },
    set(list) {
      localStorage.setItem(KEY, JSON.stringify(Array.from(new Set(list))))
      emit("lbj:wishlist:change")
    },
    has(h) {
      return state.get().includes(h)
    },
    add(h) {
      const l = state.get()
      if (!l.includes(h)) {
        l.push(h)
        state.set(l)
      }
    },
    remove(h) {
      state.set(state.get().filter((x) => x !== h))
    },
    toggle(h) {
      state.has(h) ? state.remove(h) : state.add(h)
    },
  }

  // --------------- utils -----------------
  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail }))
  }
  function $$(sel, ctx) {
    return Array.from((ctx || document).querySelectorAll(sel))
  }

  function getHandleFrom(el) {
    if (!el) return null
    let h = el.dataset?.productHandle || el.dataset?.handle
    if (h) return h
    h = el.getAttribute?.("data-product-handle") || el.getAttribute?.("data-handle")
    if (h) return h
    let n = el.parentElement,
      i = 0
    while (n && i++ < 5) {
      h = n.dataset?.productHandle || n.dataset?.handle
      if (h) return h
      h = n.getAttribute?.("data-product-handle") || n.getAttribute?.("data-handle")
      if (h) return h
      n = n.parentElement
    }
    return null
  }

  function hydrateButtons() {
    $$(S.btn).forEach((btn) => {
      const h = getHandleFrom(btn)
      if (!h) return
      const on = state.has(h)
      btn.classList.toggle("is-active", on)
      btn.setAttribute("aria-pressed", String(on))
    })
  }

  // Count + header bubble updater (idempotent)
  function updateCount() {
    const count = state.get().length

    // DO NOT update any text nodes - bubble is completely removed
    // Only toggle has-items class on header icon for red fill effect
    document.querySelectorAll(".header-actions__wishlist-icon").forEach((btn) => {
      btn.classList.toggle("has-items", count > 0)
      // Remove any bubble elements if they exist
      btn.querySelectorAll(".wishlist-bubble").forEach((bubble) => bubble.remove())
    })
  }

  // Reveal bubble only after count is drawn to avoid flash
  let revealed = false
  function revealWhenStable() {
    if (revealed) return
    updateCount()
    const doReveal = () => {
      if (!revealed) {
        document.documentElement.classList.add(READY_CLASS)
        revealed = true
      }
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(doReveal, 120)
      })
    })
  }

  // --------------- data ------------------
  function fetchProduct(handle) {
    return fetch(`/products/${handle}.js`, { credentials: "same-origin" }).then((r) => {
      if (!r.ok) throw new Error("fetch failed")
      return r.json()
    })
  }

  function formatMoney(cents) {
    const n = Number(cents) / 100
    try {
      const ccy = "AUD" // Default currency if Shopify variable is undeclared
      return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n)
    } catch (_) {
      return `$${n.toFixed(2)}`
    }
  }

  // -------------- drawer -----------------
  function drawerRoot() {
    return document.querySelector(S.drawer)
  }
  function drawerDialog() {
    return document.querySelector(S.dialog)
  }
  function isOpen() {
    return drawerRoot()?.getAttribute("aria-hidden") === "false"
  }

  function openWishlist() {
    const root = drawerRoot()
    if (!root) return
    const dlg = drawerDialog()
    if (dlg) {
      dlg.removeAttribute("inert")
      try {
        ;(dlg.showModal || dlg.show || Function.prototype).call(dlg)
      } catch (_) {
        /* noop */
      }
      if (!dlg._lbjBackdropBound) {
        dlg.addEventListener("click", (ev) => {
          if (ev.target === dlg) closeWishlist()
        })
        dlg._lbjBackdropBound = true
      }
    }
    root.setAttribute("aria-hidden", "false")
    root.classList.add("cart-drawer--open")
    document.documentElement.classList.add("wishlist-open")

    const grid = root.querySelector(S.grid)
    if (grid) renderGrid(grid)
  }

  function closeWishlist() {
    const root = drawerRoot()
    if (!root) return
    const dlg = drawerDialog()
    if (dlg && dlg.open && typeof dlg.close === "function") {
      try {
        dlg.close()
      } catch (_) {
        /* noop */
      }
    }
    root.setAttribute("aria-hidden", "true")
    root.classList.remove("cart-drawer--open")
    document.documentElement.classList.remove("wishlist-open")
  }

  // -------------- render -----------------
  async function renderGrid(grid) {
    const handles = state.get()
    const empty = document.querySelector(S.empty)

    if (handles.length === 0) {
      grid.innerHTML = ""
      if (empty) empty.hidden = false
      return
    }
    if (empty) empty.hidden = true

    grid.innerHTML = `<div class="lbj-wl-loading">Loading…</div>`
    try {
      const results = await Promise.allSettled(handles.map(fetchProduct))

      const ok = results
        .filter(r => r.status === "fulfilled")
        .map(r => r.value)

      const badHandles = results
        .map((r, i) => (r.status === "rejected" ? handles[i] : null))
        .filter(Boolean)

      // Auto-remove broken items so it won’t keep failing forever
      if (badHandles.length) {
        const cleaned = handles.filter(h => !badHandles.includes(h))
        state.set(cleaned)
      }

      if (!ok.length) {
        grid.innerHTML = ""
        if (empty) empty.hidden = false
        return
      }

      grid.innerHTML = ok
        .map((p) => {
          const v = p.variants.find((v) => v.available) || p.variants[0]
          const price = v ? formatMoney(v.price) : ""
          const img = p.images && p.images[0] ? p.images[0] : p.featured_image || ""
          return `
            <article class="lbj-wl-card" data-handle="${p.handle}">
              <a class="lbj-wl-image" href="/products/${p.handle}">
                ${img ? `<img loading="lazy" src="${img}" alt="${p.title}">` : ``}
              </a>
              <div class="lbj-wl-info">
                <a class="lbj-wl-title" href="/products/${p.handle}">${p.title}</a>
                <div class="lbj-wl-price">${price}</div>
                <div class="lbj-wl-actions">
                  ${v ? `<button class="lbj-wl-add" data-variant-id="${v.id}">Add to Bag <span class="chevron">›</span></button>` : ``}
                  <button class="lbj-wl-remove" data-remove="${p.handle}">Remove <span class="chevron">›</span></button>
                </div>
              </div>
            </article>`
        })
        .join("")


      grid.insertAdjacentHTML(
        "beforeend",
        `<a class="lbj-wl-continue" href="/collections/all">Continue Shopping <span class="chevron">›</span></a>`,
      )

      grid.querySelectorAll(".lbj-wl-remove").forEach((btn) =>
        btn.addEventListener("click", (e) => {
          e.preventDefault()
          e.stopPropagation()
          state.remove(e.currentTarget.dataset.remove)
          renderGrid(grid)
          hydrateButtons()
          updateCount()
        }),
      )

      grid.querySelectorAll(".lbj-wl-add").forEach((btn) =>
        btn.addEventListener("click", async (e) => {
          e.preventDefault()
          e.stopPropagation()
          const id = e.currentTarget.dataset.variantId
          btn.disabled = true
          try {
            await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id, quantity: 1 }),
            })

            if (window.cartDrawer) {
              await window.cartDrawer.refresh()
            }

            toast("Added to bag")
          } catch (_) {
            toast("Error")
          } finally {
            btn.disabled = false
          }
        }),
      )
    } catch (_) {
      grid.innerHTML = `<p class="lbj-wl-error">Could not load wishlist.</p>`
    }
  }

  // -------------- toast ------------------
  function toast(msg) {
    const t = document.querySelector(S.toast)
    if (!t) return
    t.textContent = msg
    t.hidden = false
    clearTimeout(t._tid)
    t._tid = setTimeout(() => (t.hidden = true), 1800)
  }

  // ---- header wishlist icon (shadow-aware) ----
  function getHeaderWishlistIcon() {
    let el = document.querySelector(".header-actions__wishlist-icon")
    if (el) return el

    const host = document.querySelector("header-actions")
    if (host?.shadowRoot) {
      el = host.shadowRoot.querySelector(".header-actions__wishlist-icon")
      if (el) return el
    }
    const all = document.querySelectorAll("*")
    for (const node of all) {
      if (node.shadowRoot) {
        el = node.shadowRoot.querySelector(".header-actions__wishlist-icon")
        if (el) return el
      }
    }
    return null
  }

  // -------------- fly animation (add only) --------------
  function flyHeartToHeader(sourceBtn) {
    try {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

      const headerBtn = getHeaderWishlistIcon()
      const svg = sourceBtn.querySelector(".wl-icon, svg")
      if (!headerBtn || !svg) return

      const src = svg.getBoundingClientRect()
      const dest = headerBtn.getBoundingClientRect()

      const clone = document.createElement("div")
      clone.className = "wl-fly-heart"
      clone.style.cssText = `
        position:fixed; left:0; top:0; width:22px; height:22px; z-index:9999;
        pointer-events:none; will-change:transform,opacity;
      `
      clone.innerHTML = svg.outerHTML
      document.body.appendChild(clone)

      const startX = src.left + src.width / 2 - 11
      const startY = src.top + src.height / 2 - 11
      const endX = dest.left + dest.width / 2 - 11
      const endY = dest.top + dest.height / 2 - 11

      clone.animate(
        [
          { transform: `translate(${startX}px, ${startY}px) scale(1)`, opacity: 1 },
          {
            transform: `translate(${(startX + endX) / 2}px, ${Math.min(startY, endY) - 80}px) scale(.9)`,
            opacity: 0.95,
          },
          { transform: `translate(${endX}px, ${endY}px) scale(.6)`, opacity: 0 },
        ],
        {
          duration: 700,
          easing: "cubic-bezier(.22,1,.36,1)",
          fill: "forwards",
        },
      ).onfinish = () => {
        clone.remove()
        const bubble = headerBtn.querySelector(".wishlist-bubble")
        if (bubble) {
          bubble.classList.remove("wl-pulse")
          // restart pulse
          // eslint-disable-next-line no-unused-expressions
          bubble.offsetWidth
          bubble.classList.add("wl-pulse")
        }
      }
    } catch (_) {
      /* noop */
    }
  }

  // ---------------- events ----------------
  document.addEventListener(
    "click",
    (e) => {
      const btn = e.target.closest(S.btn)
      if (btn) {
        e.preventDefault()
        e.stopPropagation()

        const h = getHandleFrom(btn)
        if (!h) return

        try {
          state.toggle(h)

          const isActive = state.has(h)
          btn.classList.toggle("is-active", isActive)
          btn.setAttribute("aria-pressed", String(isActive))

          void btn.offsetHeight

          // Defer sync work
          requestAnimationFrame(() => {
            hydrateButtons()
            updateCount()
            if (isActive) requestAnimationFrame(() => flyHeartToHeader(btn))

            const root = drawerRoot()
            const grid = (root && isOpen() && root.querySelector(S.grid)) || document.querySelector(S.grid)
            if (grid) renderGrid(grid)
          })

          toast(
            isActive
              ? btn.dataset.addedText || "Added to wishlist"
              : btn.dataset.removedText || "Removed from wishlist",
          )
        } catch (err) {
          console.error("LBJ Wishlist toggle error:", err)
        }
        return
      }

      // Open/close drawer
      if (e.target.closest(S.open)) {
        openWishlist()
        return
      }
      if (e.target.closest(S.close)) {
        closeWishlist()
        return
      }
    },
    { passive: false },
  )

  // ESC closes
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeWishlist()
  })

  // Debounced DOM mutations (sliders, AJAX inserts)
  let moTimer = null
  const mo = new MutationObserver(() => {
    clearTimeout(moTimer)
    moTimer = setTimeout(() => {
      hydrateButtons()
      updateCount()
    }, 50)
  })
  mo.observe(document.documentElement, { childList: true, subtree: true })

  // React to external updates
  document.addEventListener("lbj:wishlist:change", () => {
    hydrateButtons()
    updateCount()
    if (isOpen()) {
      const root = drawerRoot()
      const grid = root && root.querySelector(S.grid)
      if (grid) renderGrid(grid)
    }
  })

  // Defensive backdrop close for <dialog>
  document.addEventListener("click", (e) => {
    const dlg = drawerDialog()
    if (!dlg || !dlg.open) return
    if (e.target === dlg) closeWishlist()
  })

  // --------------- init -------------------
  function initWishlist() {
    try {
      hydrateButtons()
      revealWhenStable()

      if (isOpen()) {
        const root = drawerRoot()
        const grid = root && root.querySelector(S.grid)
        if (grid) renderGrid(grid)
      }

      // Import from ?wl=handle,handle
      const wl = new URL(location.href).searchParams.get("wl")
      if (wl) {
        const incoming = wl
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
        if (incoming.length) {
          state.set([...new Set([...state.get(), ...incoming])])
          hydrateButtons()
          updateCount()
          const root = drawerRoot()
          const grid = (root && root.querySelector(S.grid)) || document.querySelector(S.grid)
          if (grid) renderGrid(grid)
        }
      }

      // Hard-bind open/close (non-delegated)
      document.querySelectorAll(S.open).forEach((el) => {
        el.addEventListener("click", (e) => {
          e.preventDefault()
          openWishlist()
        })
      })
      document.querySelectorAll(S.close).forEach((el) => {
        el.addEventListener("click", (e) => {
          e.preventDefault()
          closeWishlist()
        })
      })
    } catch (e) {
      console.error("LBJ Wishlist init error:", e)
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initWishlist, { once: true })
  } else {
    initWishlist()
  }

  // Keep count in sync on visibility/page cache restore
  window.addEventListener("pageshow", () => updateCount())
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") updateCount()
  })

  // export
  window.LBJWishlist = {
    get: state.get,
    toggle: state.toggle,
    add: state.add,
    remove: state.remove,
    open: openWishlist,
    close: closeWishlist,
  }
})()
